/**
 * Title-first ranking for PubMed search results
 *
 * Scores results based on whether search terms appear in the title,
 * prioritizing papers with title matches over those with only abstract matches.
 */

import type { PubmedPaperSummary } from '../../../../../services/pubmedService';

export interface RankedPaper extends PubmedPaperSummary {
  titleScore: number;
  originalIndex: number;
}

/**
 * Extract search terms from a PubMed query
 * Removes operators, field tags, and returns individual terms
 *
 * @param query - Raw PubMed search query
 * @returns Array of search terms to look for in title
 */
export function extractSearchTerms(query: string): string[] {
  // Remove PubMed field tags like [tiab], [mh], [au], etc.
  let cleaned = query.replace(/\[[\w]+\]/g, '');

  // Remove explicit operators and parentheses (but keep the text)
  cleaned = cleaned
    .replace(/\bAND\b/gi, ' ')
    .replace(/\bOR\b/gi, ' ')
    .replace(/\bNOT\b/gi, ' ')
    .replace(/[()]/g, ' ');

  // Split into individual terms and filter out empty/stop words
  const terms = cleaned
    .split(/\s+/)
    .map(t => t.trim().toLowerCase())
    .filter(t => t.length > 0 && t.length > 2); // Filter short terms (noise)

  // Remove duplicates
  return [...new Set(terms)];
}

/**
 * Calculate a title score for a paper based on search term matches
 *
 * Scoring:
 * - Exact word match in title (case-insensitive): +100
 * - Partial match in title (as substring): +50
 * - No match: 0
 *
 * @param title - Article title
 * @param searchTerms - Terms to search for
 * @returns Numeric score (higher = better match)
 */
export function calculateTitleScore(title: string, searchTerms: string[]): number {
  if (!title || searchTerms.length === 0) {
    return 0;
  }

  const titleLower = title.toLowerCase();
  const titleWords = titleLower.split(/\b/).filter(w => w.length > 0);
  let score = 0;

  for (const term of searchTerms) {
    // Exact word match: +100 per match
    if (titleWords.includes(term)) {
      score += 100;
    }
    // Substring match: +50 per match (but not if already counted as word match)
    else if (titleLower.includes(term)) {
      score += 50;
    }
  }

  return score;
}

/**
 * Rank papers by title relevance
 *
 * Papers with search terms in the title appear first (sorted by score descending).
 * Papers without title matches appear last, in original API order.
 *
 * @param papers - Array of PubMed paper summaries
 * @param query - Original search query
 * @returns Ranked array with papers scored and reordered
 */
export function rankByTitle(
  papers: PubmedPaperSummary[],
  query: string
): PubmedPaperSummary[] {
  const searchTerms = extractSearchTerms(query);

  // If no valid search terms extracted, return original order
  if (searchTerms.length === 0) {
    return papers;
  }

  // Score each paper
  const rankedPapers: RankedPaper[] = papers.map((paper, index) => ({
    ...paper,
    titleScore: calculateTitleScore(paper.title, searchTerms),
    originalIndex: index,
  }));

  // Sort by title score (descending), then by original API order
  rankedPapers.sort((a, b) => {
    if (b.titleScore !== a.titleScore) {
      return b.titleScore - a.titleScore; // Higher score first
    }
    return a.originalIndex - b.originalIndex; // Original order as tiebreaker
  });

  // Remove ranking metadata before returning
  return rankedPapers.map(({ titleScore, originalIndex, ...paper }) => paper);
}
